import { directUpload } from '@/services/api';
import { formatS3Url } from '@/utils/s3';

/**
 * Applies a solid color background to an image and uploads the result
 * @param imageUrl - URL of the source image
 * @param imageId - ID for the uploaded image, the folder name will be the imageId (usually the presenterId)
 * @param backgroundColor - CSS color string for the background
 * @param fileName - Name for the output file
 * @returns Promise resolving to the URL of the uploaded image
 */
interface ApplyBackgroundColorParams {
    imageUrl: string;
    imageId: string;
    backgroundColor: string;
    fileName: string;
    bucket: string;
    dns: string;
}

export const applyBackgroundColor = async ({
    imageUrl,
    imageId,
    backgroundColor,
    fileName,
    bucket,
    dns,
}: ApplyBackgroundColorParams): Promise<string> => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    return new Promise((resolve, reject) => {
        img.onload = async () => {
            const canvas = document.createElement('canvas');

            if (img.width === img.height) {
                canvas.width = img.width * (16 / 9);
                canvas.height = img.height;
            } else {
                canvas.width = img.width;
                canvas.height = img.height;
            }

            const ctx = canvas.getContext('2d');
            if (!ctx) {
                reject(new Error('Failed to get canvas context'));
                return;
            }
            ctx.fillStyle = backgroundColor;
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            const x = (canvas.width - img.width) / 2;
            const y = (canvas.height - img.height) / 2;

            ctx.drawImage(img, x, y);

            try {
                const blob = await new Promise<Blob>((resolve, reject) => {
                    canvas.toBlob(blob => (blob ? resolve(blob) : reject(new Error('Failed to create blob'))), 'image/png');
                });
                const file = new File([blob], fileName, { type: 'image/png' });

                const { url } = await directUpload({ file, metadata: { bucket, image_id: imageId } });
                if (!url) {
                    throw new Error('Failed to upload image');
                }
                const formattedUrl = formatS3Url(dns, url);
                resolve(formattedUrl);
            } catch (error) {
                reject(error);
            }
        };

        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = imageUrl;
    });
};
